import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { ArrowRight, RotateCcw, ScanSearch, TrendingDown, TrendingUp, Minus, Lock } from 'lucide-react';

const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
const pct = (from, to) => (from ? ((to - from) / from) * 100 : null);

const DeltaPill = ({ from, to, unit = '', symbol = '', decimals = 0, testId }) => {
  const diff = to - from;
  const p = pct(from, to);
  const dir = Math.abs(diff) < 0.01 ? 'flat' : diff > 0 ? 'up' : 'down';
  const cls = dir === 'flat' ? 'bg-slate-100 text-slate-600 border-slate-200'
    : dir === 'up' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const Icon = dir === 'flat' ? Minus : dir === 'up' ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${cls}`} data-testid={testId}>
      <Icon className="w-3 h-3" />
      {diff > 0 ? '+' : diff < 0 ? '−' : ''}{symbol}{fmtNum(Math.abs(diff), decimals)}{unit}
      {p !== null && dir !== 'flat' && <span className="opacity-70">({p > 0 ? '+' : ''}{p.toFixed(1)}%)</span>}
    </span>
  );
};

const TotalRow = ({ label, from, to, symbol = '', unit = '', decimals = 0, testId }) => (
  <div className="flex items-center justify-between gap-4 py-2.5" data-testid={testId}>
    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 w-20">{label}</span>
    <div className="flex items-center gap-2 text-sm">
      <span className="text-slate-500 tabular-nums" data-testid={`${testId}-saved`}>{symbol}{fmtNum(from, decimals)}{unit}</span>
      <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
      <span className="font-bold text-[#0F2040] tabular-nums" data-testid={`${testId}-new`}>{symbol}{fmtNum(to, decimals)}{unit}</span>
    </div>
    <DeltaPill from={from} to={to} symbol={symbol} unit={unit} decimals={decimals} testId={`${testId}-delta`} />
  </div>
);

/**
 * Pre-save review: summarises effort / cost / setting changes versus the
 * last saved values, with per-field and whole-estimate restore actions.
 *
 * Props:
 *  open, onClose(), onConfirm(), onRestoreAll(), onRestoreField(key)
 *  review: { totals: {effortFrom, effortTo, costFrom, costTo, symbol},
 *            settings: [{key, label, from, to}],
 *            components: [{name, kind:'added'|'removed'|'changed', effortFrom, effortTo, costFrom, costTo}] }
 *  saving boolean, versionLabel string
 */
export function SaveReviewDialog({ open, onClose, onConfirm, onRestoreAll, onRestoreField, review, saving, versionLabel, lock }) {
  const [note, setNote] = useState('');
  useEffect(() => { if (open) setNote(''); }, [open]);
  if (!review) return null;
  const lockBlocked = !!review.lockBlocked;
  const { totals, settings = [], components = [] } = review;
  const symbol = totals?.symbol || '$';
  const hasTotals = totals && (totals.effortFrom != null || totals.costFrom != null);
  const effortChanged = hasTotals && Math.abs((totals.effortTo || 0) - (totals.effortFrom || 0)) >= 0.05;
  const costChanged = hasTotals && Math.abs((totals.costTo || 0) - (totals.costFrom || 0)) >= 0.5;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" aria-describedby="save-review-description" data-testid="save-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
            <ScanSearch className="w-5 h-5 text-[#4ABFCF]" />
            Review changes before saving
          </DialogTitle>
          <DialogDescription id="save-review-description">
            These values differ from the last saved {versionLabel || 'version'}. Confirm to commit them, or restore the saved values if a change was made in error.
          </DialogDescription>
        </DialogHeader>

        {lockBlocked && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-800" data-testid="save-review-lock-notice">
            <Lock className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              <span className="font-semibold">Effort &amp; cost are locked</span>
              {lock?.by_name ? ` by ${lock.by_name}` : ''}{lock?.at ? ` on ${new Date(lock.at).toLocaleDateString()}` : ''}{lock?.reason ? ` — “${lock.reason}”` : ''}.
              {' '}These changes cannot be saved. Restore the saved values, or unlock the estimate first (owner, CCO, CRO or Super Admin).
            </p>
          </div>
        )}

        {hasTotals && (
          <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-4 divide-y divide-slate-200" data-testid="save-review-totals">
            <TotalRow label="Effort" from={totals.effortFrom || 0} to={totals.effortTo || 0} unit="h" decimals={1} testId="save-review-effort" />
            <TotalRow label="Cost" from={totals.costFrom || 0} to={totals.costTo || 0} symbol={symbol} testId="save-review-cost" />
            {!effortChanged && !costChanged && (
              <p className="text-[11px] text-slate-500 py-2" data-testid="save-review-totals-unchanged">Effort and cost totals are unchanged; only the settings below differ.</p>
            )}
          </div>
        )}

        {settings.length > 0 && (
          <div data-testid="save-review-settings">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Estimate settings</p>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {settings.map((s) => (
                <li key={s.key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`save-review-setting-${s.key}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-600">{s.label}</p>
                    <p className="flex items-center gap-1.5 text-xs truncate">
                      <span className="text-slate-500 line-through decoration-slate-300" data-testid={`save-review-setting-${s.key}-saved`}>{s.from ?? '—'}</span>
                      <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="font-semibold text-[#0F2040]" data-testid={`save-review-setting-${s.key}-new`}>{s.to ?? '—'}</span>
                    </p>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    className="h-7 px-2 text-xs text-amber-700 hover:text-amber-800 hover:bg-amber-50 shrink-0"
                    onClick={() => onRestoreField(s.key)}
                    disabled={saving}
                    data-testid={`save-review-restore-${s.key}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {components.length > 0 && (
          <div data-testid="save-review-components">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Component changes ({components.length})</p>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-56 overflow-y-auto">
              {components.map((c) => (
                <li key={c.name} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs" data-testid={`save-review-component-${c.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    <span className={`text-[9px] font-bold uppercase rounded px-1 py-0 border ${
                      c.kind === 'added' ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : c.kind === 'removed' ? 'bg-slate-100 text-slate-600 border-slate-200'
                          : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{c.kind}</span>
                    <span className="truncate text-slate-700 font-medium">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 tabular-nums text-slate-600">
                    <span>{fmtNum(c.effortFrom, 1)}h <ArrowRight className="inline w-3 h-3 text-slate-400" /> <b className="text-[#0F2040]">{fmtNum(c.effortTo, 1)}h</b></span>
                    <span>{symbol}{fmtNum(c.costFrom)} <ArrowRight className="inline w-3 h-3 text-slate-400" /> <b className="text-[#0F2040]">{symbol}{fmtNum(c.costTo)}</b></span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div data-testid="save-review-note">
          <Label htmlFor="save-review-note-input" className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Change note <span className="font-normal normal-case text-slate-400">· optional, stored in the version audit trail</span></Label>
          <Textarea
            id="save-review-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 300))}
            placeholder="Why are these values changing? e.g. Switched contracting entity to EMEA at client's request"
            rows={2}
            className="mt-1 text-sm resize-none"
            disabled={saving}
            data-testid="save-review-note-input"
          />
          <p className="text-[10px] text-slate-400 text-right mt-0.5" data-testid="save-review-note-count">{note.length}/300</p>
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onRestoreAll}
            disabled={saving}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="save-review-restore-all-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore saved values
          </Button>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="save-review-cancel-btn">Keep editing</Button>
            <Button onClick={() => onConfirm(note.trim())} disabled={saving || lockBlocked} title={lockBlocked ? 'Effort & cost are locked' : undefined} className="bg-[#4ABFCF] hover:bg-[#3aa8b8] text-white" data-testid="save-review-confirm-btn">
              {saving ? 'Saving...' : 'Confirm & Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
